import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { FiSearch, FiFilter } from 'react-icons/fi';
import Button from '../common/Button';
import Card from '../common/Card';
import { reportService, adminService } from '../../services/api';

// Register the ChartJS pieces the trend chart needs (module level, once).
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const ReportContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ReportTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  margin: 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;
  min-width: 150px;
`;

const FilterInput = styled.input`
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;
`;

const FilterActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-left: auto;
`;

// Matrix table — horizontally scrollable, one column per week.
const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
`;

// The Employee column stays put while the week columns scroll (feedback #4).
const StickyHeadCell = styled(TableHeadCell)`
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  box-shadow: inset -1px 0 0 ${({ theme }) => theme.colors.border};
`;

const WeekHeadCell = styled(TableHeadCell)`
  text-align: right;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const CustomerRow = styled.tr`
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
`;

const CustomerCell = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[1]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
`;

// The customer name sticks to the left edge while the wide row scrolls
// (the cell spans every column, so the sticky element is the inner span).
const CustomerName = styled.span`
  position: sticky;
  left: ${({ theme }) => theme.spacing[3]};
  display: inline-block;
`;

// Inactive employees are muted (REQ #19). Sticky first column (feedback #4):
// a solid background so the scrolling week cells pass underneath it.
const NameCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ inactive, theme }) => (inactive ? theme.colors.textSecondary : theme.colors.text)};
  font-style: ${({ inactive }) => (inactive ? 'italic' : 'normal')};
  white-space: nowrap;
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: white;
  box-shadow: inset -1px 0 0 ${({ theme }) => theme.colors.border};
`;

const HoursCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ inactive, theme }) => (inactive ? theme.colors.textSecondary : theme.colors.text)};
  text-align: right;
  white-space: nowrap;
`;

const TotalCell = styled(HoursCell)`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: ${({ theme }) => theme.colors.error}15;
  color: ${({ theme }) => theme.colors.error};
`;

// Trend chart (REQ #19b)
const ChartSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing[5]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  margin: 0;
`;

const ChartContainer = styled.div`
  position: relative;
  height: 320px;
`;

// Format a Date as a local 'YYYY-MM-DD' string (no UTC shift / off-by-one)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Default period: the Monday 8 weeks back (this week included) → today.
const defaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) - 7 * 7);
  return formatLocalDate(d);
};

const formatHours = (hours) =>
  hours == null ? '' : (Math.round(hours * 100) / 100).toLocaleString();

const WeeklyHoursView = () => {
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    startDate: defaultStartDate(),
    endDate: formatLocalDate(new Date()),
    customerId: '',
    active: '',
    employeeId: ''
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trendMode, setTrendMode] = useState('aggregated');

  // Fetch customers for the filter dropdown: those active in the selected
  // period (hours or an assignment overlapping it), sorted alphabetically.
  // Clears a selection that drops out of the new list.
  React.useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customersResponse = await adminService.getCustomers({
          ...(filters.startDate && filters.endDate && { activeFrom: filters.startDate, activeTo: filters.endDate }),
          limit: 1000
        });
        const customerList = (customersResponse.customers || [])
          .slice()
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
        setCustomers(customerList);
        setFilters(prev => (
          prev.customerId && !customerList.some(c => String(c.id) === String(prev.customerId))
            ? { ...prev, customerId: '' }
            : prev
        ));
      } catch (err) {
        console.error('Error fetching filter data:', err);
      }
    };

    fetchCustomers();
  }, [filters.startDate, filters.endDate]);

  // Fetch employees scoped to the selected customer and period, sorted
  // alphabetically; clear the selected employee when it drops out.
  React.useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeesResponse = await adminService.getEmployees({
          ...(filters.customerId && { customerId: filters.customerId }),
          ...(filters.startDate && filters.endDate && { activeFrom: filters.startDate, activeTo: filters.endDate }),
          limit: 1000,
          page: 1
        });
        const employeeList = (employeesResponse.employees || [])
          .slice()
          .sort((a, b) =>
            `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
              `${b.firstName || ''} ${b.lastName || ''}`,
              undefined,
              { sensitivity: 'base' }
            )
          );
        setEmployees(employeeList);
        setFilters(prev => (
          prev.employeeId && !employeeList.some(emp => String(emp.id) === String(prev.employeeId))
            ? { ...prev, employeeId: '' }
            : prev
        ));
      } catch (err) {
        console.error('Error fetching filter data:', err);
      }
    };

    fetchEmployees();
  }, [filters.customerId, filters.startDate, filters.endDate]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportService.getWeeklyHoursReport({
        startDate: filters.startDate,
        endDate: filters.endDate,
        ...(filters.customerId && { customerId: filters.customerId }),
        ...(filters.active && { active: filters.active }),
        ...(filters.employeeId && { employeeIds: String(filters.employeeId) })
      });
      setReportData(data);
    } catch (err) {
      console.error('Error generating weekly hours report:', err);
      setError(err.message || 'Failed to generate the report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const weeks = reportData?.weeks || [];

  // All employees in the filtered result, merged across customers by userId
  // (an employee that logged hours for two customers is one trend line).
  const mergedEmployees = React.useMemo(() => {
    const byUser = new Map();
    (reportData?.customers || []).forEach(customer => {
      (customer.employees || []).forEach(emp => {
        if (!byUser.has(emp.userId)) {
          byUser.set(emp.userId, { userId: emp.userId, name: emp.name, isActive: emp.isActive, weeks: {} });
        }
        const merged = byUser.get(emp.userId);
        Object.entries(emp.weeks || {}).forEach(([weekKey, hours]) => {
          merged.weeks[weekKey] = (merged.weeks[weekKey] || 0) + hours;
        });
      });
    });
    return [...byUser.values()].sort((a, b) =>
      a.name.localeCompare(b.name, ['sv', 'da'], { sensitivity: 'base' })
    );
  }, [reportData]);

  const chartData = React.useMemo(() => {
    if (!reportData) return null;
    const labels = weeks.map(week => `W${week.weekNo}`);

    if (trendMode === 'individual') {
      const count = mergedEmployees.length || 1;
      return {
        labels,
        datasets: mergedEmployees.map((emp, index) => {
          const color = `hsl(${Math.round((360 * index) / count)}, 65%, 45%)`;
          return {
            label: emp.name,
            data: weeks.map(week => emp.weeks[week.key] || 0),
            borderColor: color,
            backgroundColor: color,
            borderWidth: 2,
            pointRadius: 3,
            tension: 0.1,
            fill: false
          };
        })
      };
    }

    // Aggregated: average hours per employee per week; employees without
    // hours in a week count as 0.
    const employeeCount = mergedEmployees.length;
    return {
      labels,
      datasets: [
        {
          label: 'Average hours per employee',
          data: weeks.map(week => {
            if (employeeCount === 0) return 0;
            const sum = mergedEmployees.reduce((acc, emp) => acc + (emp.weeks[week.key] || 0), 0);
            return Math.round((sum / employeeCount) * 100) / 100;
          }),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgb(54, 162, 235)',
          borderWidth: 2,
          pointRadius: 3,
          tension: 0.1,
          fill: false
        }
      ]
    };
  }, [reportData, weeks, mergedEmployees, trendMode]);

  const chartOptions = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: trendMode === 'individual',
        position: 'bottom'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  }), [trendMode]);

  return (
    <ReportContainer>
      <ReportHeader>
        <ReportTitle>Weekly Hours</ReportTitle>
      </ReportHeader>

      <Card padding="md">
        <FiltersContainer>
          <FilterGroup>
            <FilterLabel htmlFor="weekly-startDate">Start Date</FilterLabel>
            <FilterInput
              type="date"
              id="weekly-startDate"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="weekly-endDate">End Date</FilterLabel>
            <FilterInput
              type="date"
              id="weekly-endDate"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="weekly-customerId">Customer</FilterLabel>
            <FilterSelect
              id="weekly-customerId"
              name="customerId"
              value={filters.customerId}
              onChange={handleFilterChange}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="weekly-active">Employee Status</FilterLabel>
            <FilterSelect
              id="weekly-active"
              name="active"
              value={filters.active}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="weekly-employeeId">Employee</FilterLabel>
            <FilterSelect
              id="weekly-employeeId"
              name="employeeId"
              value={filters.employeeId}
              onChange={handleFilterChange}
            >
              <option value="">All Employees</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.firstName} {employee.lastName}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterActions>
            <Button
              onClick={handleGenerate}
              disabled={loading}
            >
              <FiSearch style={{ marginRight: '8px' }} />
              {loading ? 'Generating...' : 'Generate'}
            </Button>
          </FilterActions>
        </FiltersContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {loading ? (
          <EmptyState>Generating report...</EmptyState>
        ) : reportData ? (
          (reportData.customers || []).length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <tr>
                      <StickyHeadCell>Employee</StickyHeadCell>
                      {weeks.map(week => (
                        <WeekHeadCell key={week.key} title={`Week starting ${week.start}`}>
                          W{week.weekNo}
                        </WeekHeadCell>
                      ))}
                      <WeekHeadCell>Total</WeekHeadCell>
                    </tr>
                  </TableHead>
                  <tbody>
                    {reportData.customers.map(customer => (
                      <React.Fragment key={customer.customerId ?? customer.customerName}>
                        <CustomerRow>
                          <CustomerCell colSpan={weeks.length + 2}>
                            <CustomerName>{customer.customerName}</CustomerName>
                          </CustomerCell>
                        </CustomerRow>
                        {customer.employees.map(emp => (
                          <TableRow key={emp.userId}>
                            <NameCell inactive={!emp.isActive} title={emp.isActive ? undefined : 'Inactive employee'}>
                              {emp.name}
                            </NameCell>
                            {weeks.map(week => (
                              <HoursCell key={week.key} inactive={!emp.isActive}>
                                {formatHours(emp.weeks?.[week.key])}
                              </HoursCell>
                            ))}
                            <TotalCell inactive={!emp.isActive}>
                              {formatHours(emp.totalHours)}
                            </TotalCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>

              <ChartSection>
                <ChartHeader>
                  <ChartTitle>Weekly hours trend</ChartTitle>
                  <FilterGroup>
                    <FilterLabel htmlFor="weekly-trendMode">Chart</FilterLabel>
                    <FilterSelect
                      id="weekly-trendMode"
                      value={trendMode}
                      onChange={(e) => setTrendMode(e.target.value)}
                    >
                      <option value="aggregated">Aggregated</option>
                      <option value="individual">Individual</option>
                    </FilterSelect>
                  </FilterGroup>
                </ChartHeader>
                <ChartContainer>
                  {chartData && <Line data={chartData} options={chartOptions} />}
                </ChartContainer>
              </ChartSection>
            </>
          ) : (
            <EmptyState>No hours found for the selected criteria.</EmptyState>
          )
        ) : (
          <EmptyState>
            <FiFilter style={{ marginBottom: '8px', opacity: 0.5 }} />
            <p>Select filters and generate a report to see weekly hours</p>
          </EmptyState>
        )}
      </Card>
    </ReportContainer>
  );
};

export default WeeklyHoursView;
